// EventRow.tsx — one pipeline event, shared by the Activity page, the wall,
// and the per-PR events panel. One line: when, which tool reported it as a
// small icon, what happened in plain words,
// the summary with its subject as the link (minus a duration the
// column beside it already says), how long it took — with a thin bar
// behind the duration, scaled against the longest run of the same kind in the
// loaded list, so a slow gate or a long attempt shows at a glance. Everything else
// (who, which commit, why, the few fields its kind is asked about — the gate's
// runner, the attempt's model, the command that would deploy a staged release —
// the log tail, the event as the API returns it) opens below, each under the
// word naming it. A row that needs a human
// is red until a later event clears its cause; then it is marked resolved.
// A row the live tail just brought in slides down with a short wash, so the
// feed says "this is new" without moving anything else.
// A start with no finish yet is work happening now: it spins, counts the time
// it has been going, and carries a progress bar — filled against what its kind
// usually takes when that is known, indeterminate when it is not.
// The toggle stays on the rows that have something to say (a reason, a log, a
// field of its own);
// on the quiet ones it waits for the pointer or the keyboard.
// A log tail opens at its end, where the failure is, with the lines that
// report one in the danger tone, and a button that hands the whole tail over.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { PipelineEvent } from '../../api/types';
import { CopyButton } from '../../components/clipboard/CopyButton';
import { pillClass, toneClass } from '../../components/tone/tone';
import { absoluteText, clockText, isSameLocalDay, relativeText } from '../../format/when';
import { useForgeHost } from '../../hooks/useForgeHost';
import { formatCost, shortSha } from '../shift/shiftModel';
import { InlineProse } from './InlineProse';
import { badLineCount, logLines } from './logModel';
import { insideRowTabIndex } from './rowKeysModel';
import { sourceMark } from './sourceModel';
import { attemptUsage, attemptUsageText, attemptUsageTitle } from './usageModel';
import { elapsedSeconds, elapsedText, runningShare, runningTitle } from './runningModel';
import {
  durationShare,
  eventDetailFields,
  eventLabel,
  eventLinks,
  eventStationLink,
  eventJson,
  eventTone,
  exactSecondsText,
  formatSeconds,
  hasDetail,
  summaryParts,
  summaryText,
} from './activityModel';

import './Activity.css';

export function EventRow({
  event,
  defaultOpen = false,
  open: controlledOpen,
  focused,
  tabStop = false,
  onRest,
  resolved = false,
  arriving = false,
  longestSeconds,
  now,
  running = false,
  medianSeconds,
  onOpenChange,
}: {
  event: PipelineEvent;
  defaultOpen?: boolean;
  /** Open from outside: a list whose keyboard expands the focused row holds
   *  the state for every row, so the row follows it instead of its own. */
  open?: boolean;
  /** Given at all, this row is one step of a keyboard-walked list: it takes
   *  the focus itself, and shows where the keyboard is when it has it. */
  focused?: boolean;
  /** Nothing in the list is focused yet and this is where Tab should land:
   *  the keyboard has to be able to reach the list in the first place. */
  tabStop?: boolean;
  /** The row was reached some other way (a click, Tab): where the keyboard
   *  should carry on from. */
  onRest?: (seq: number) => void;
  /** A later event cleared this row's cause: no attention pill. */
  resolved?: boolean;
  /** The row was not in the previous render: slide in and hold a wash. */
  arriving?: boolean;
  /** The longest duration this row's kind reached in the loaded list: what the
   *  bar behind the duration is scaled against. Absent, the duration is text. */
  longestSeconds?: number;
  /** The list's shared clock. Given it, a row from today reads as how long ago
   *  it was and ages with the tick; an older row keeps its clock, because the
   *  day is said once in its heading and "3 days ago" would hide it. The
   *  elapsed timer of a running row reads it too. */
  now?: number;
  /** No finishing event has arrived for this start: show it as in flight. */
  running?: boolean;
  /** What this kind of work usually takes, when the loaded rows say: with it
   *  the progress bar is determinate, without it the bar only says "going". */
  medianSeconds?: number;
  /** Told when this row opens or closes: a feed holds new rows back while a
   *  reader has one open, so it has to know. */
  onOpenChange?: (seq: number, open: boolean) => void;
}): JSX.Element {
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const open = controlledOpen ?? selfOpen;
  const toggle = (): void => {
    setSelfOpen(!open);
    onOpenChange?.(event.seq, !open);
  };
  // Reported through an effect, with the close on the way out: a row that
  // leaves the list while open must not keep the feed waiting for it.
  const report = useRef(onOpenChange);
  report.current = onOpenChange;
  useEffect(() => {
    report.current?.(event.seq, open);
    return () => report.current?.(event.seq, false);
  }, [event.seq, open]);
  const needsHuman = event.needs_human && !resolved;
  // A cleared row keeps its words ("Gate failed") but not its red: what it
  // reports is history, and the colour is for what still wants an answer.
  const tone = resolved ? 'unknown' : eventTone(event);
  // Which tool reported the row, as one icon in front of the label pill: the
  // name itself stays in the facts below, and in this mark's title.
  const { Icon: SourceIcon, ...mark } = sourceMark(event.source);
  const forgeHost = useForgeHost();
  const host = forgeHost(event.repo ?? undefined);
  const summary = summaryParts(event, host);
  const others = eventLinks(event, host).filter((link) => link.to !== summary.link?.to);
  const station = eventStationLink(event.kind);
  const detailId = `activity-event-detail-${event.seq}`;
  const toggleLabel = open ? 'Hide' : event.log_tail ? 'Log' : event.reason ? 'Why' : 'More';
  const quiet = !open && !hasDetail(event);
  // One stop per row, not three: on a row the keyboard has not walked to, the
  // controls on its line stand aside for Tab.
  const insideTab = insideRowTabIndex(focused, tabStop);
  const duration = formatSeconds(event.seconds);
  // A bar behind the duration, as long as this row's share of the longest run
  // of its kind: a slow gate or a long attempt stands out without reading.
  const share = durationShare(event.seconds, longestSeconds);
  // What the attempt spent, when the producer attached it to the row.
  const usage = attemptUsage(event);
  // The tail as lines, so the ones reporting a failure can carry the tone.
  const lines = event.log_tail ? logLines(event.log_tail) : [];
  const badLines = badLineCount(lines);
  // The few `detail` keys this kind answers its question with.
  const fields = eventDetailFields(event);
  // An opened tail starts at its end: a gate writes what went wrong last, and
  // a reader who has to scroll 24rem to reach it has been handed the wrong end.
  const logRef = useRef<HTMLPreElement | null>(null);
  useEffect(() => {
    const pre = logRef.current;
    if (pre) pre.scrollTop = pre.scrollHeight;
  }, [open, event.log_tail]);
  // Today's rows read as an age; the clock is still in `title` either way.
  const whenText =
    now !== undefined && isSameLocalDay(event.ts, now)
      ? relativeText(event.ts, now)
      : clockText(event.ts);
  // In flight: the time since the start, and how far that is through a usual
  // run of its kind. A share of null is a bar that only says "still going".
  const elapsed = running ? elapsedSeconds(event.ts, now ?? Date.now()) : null;
  const progress = running ? runningShare(elapsed, medianSeconds) : null;

  return (
    <li
      className={`${toneClass('activity-row', tone)}${needsHuman ? ' is-needs-human' : ''}${
        arriving ? ' is-arriving' : ''
      }${focused ? ' is-focused' : ''}`}
      data-testid={`activity-event-${event.seq}`}
      // A row of a keyboard-walked list is the focus stop, with the roving
      // tabindex: Tab reaches the list once, and j/k move within it.
      tabIndex={focused === undefined ? undefined : focused || tabStop ? 0 : -1}
      onFocus={focused === undefined ? undefined : () => onRest?.(event.seq)}
    >
      <div className="activity-row__line">
        <time className="activity-row__time" dateTime={event.ts} title={absoluteText(event.ts)}>
          {whenText}
        </time>
        <span
          className="activity-row__source-icon"
          data-source-group={mark.group}
          data-testid={`activity-event-source-${event.seq}`}
          role="img"
          title={mark.title}
          aria-label={mark.title}
        >
          <SourceIcon size={13} aria-hidden="true" />
        </span>
        <span
          className={pillClass(tone)}
          title={event.kind}
        >
          {eventLabel(event)}
        </span>
        <span className="activity-row__summary">
          <InlineProse text={summaryText(event)} link={summary.link} linkTabIndex={insideTab} />
        </span>
        {needsHuman ? <span className={pillClass('human')}>needs you</span> : null}
        {event.needs_human && resolved ? (
          <span className="page__pill" title="A later event cleared this">
            resolved
          </span>
        ) : null}
        {typeof event.cost_usd === 'number' ? (
          <span className="activity-row__meta">{formatCost(event.cost_usd)}</span>
        ) : null}
        {usage ? (
          <span className="activity-row__meta" title={attemptUsageTitle(usage)}>
            {attemptUsageText(usage)}
          </span>
        ) : null}
        {running ? (
          <span className="activity-row__running" title={runningTitle(elapsed, medianSeconds)}>
            <span className="activity-row__spinner" aria-hidden="true" />
            <span className="activity-row__meta" data-testid={`activity-elapsed-${event.seq}`}>
              {elapsedText(elapsed)}
            </span>
          </span>
        ) : null}
        {duration ? (
          <span className="activity-row__duration" title={exactSecondsText(event.seconds)}>
            {share === null ? null : (
              <span
                className="activity-row__duration-bar"
                style={{ width: `${(share * 100).toFixed(1)}%` }}
                aria-hidden="true"
              />
            )}
            <span className="activity-row__meta">{duration}</span>
          </span>
        ) : null}
        {station ? (
          <Link
            to={station.to}
            className="activity-row__station"
            tabIndex={insideTab}
            data-testid={`activity-event-station-${event.seq}`}
            aria-label={`${station.label} for event ${event.seq}`}
          >
            {station.label}
          </Link>
        ) : null}
        <button
          type="button"
          className={`activity-row__toggle${quiet ? ' activity-row__toggle--quiet' : ''}`}
          aria-expanded={open}
          aria-controls={detailId}
          aria-label={`${toggleLabel} for event ${event.seq}`}
          tabIndex={insideTab}
          onClick={toggle}
        >
          {toggleLabel}
        </button>
      </div>
      {running ? (
        <div
          className={`activity-row__progress${progress === null ? ' activity-row__progress--waiting' : ''}`}
          role="progressbar"
          aria-label={`${eventLabel(event)} — ${runningTitle(elapsed, medianSeconds)}`}
          aria-valuemin={progress === null ? undefined : 0}
          aria-valuemax={progress === null ? undefined : 100}
          aria-valuenow={progress === null ? undefined : Math.round(progress * 100)}
          data-testid={`activity-progress-${event.seq}`}
        >
          <span
            className="activity-row__progress-fill"
            style={progress === null ? undefined : { width: `${(progress * 100).toFixed(1)}%` }}
            aria-hidden="true"
          />
        </div>
      ) : null}
      {open ? (
        <div className="activity-row__detail" id={detailId}>
          {event.reason ? (
            <p className="activity-row__reason">
              <InlineProse text={event.reason} />
            </p>
          ) : null}
          {/* What the producer attached, for the question this kind raises:
              which runner ran the gate, which model took the attempt, the
              command that would deploy what was just staged. */}
          {fields.length > 0 ? (
            <dl className="activity-row__fields" data-testid={`activity-event-fields-${event.seq}`}>
              {fields.map((field) => (
                <div className="activity-row__field" key={field.key}>
                  <dt>{field.label}</dt>
                  <dd>
                    <span
                      className={field.mono ? 'activity-row__field-mono' : undefined}
                      title={field.title}
                    >
                      {field.value}
                    </span>
                    {field.copy ? (
                      <CopyButton
                        text={field.value}
                        label={`event ${event.seq} ${field.label.toLowerCase()}`}
                        testId={`activity-event-field-copy-${event.seq}-${field.key}`}
                      >
                        Copy
                      </CopyButton>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {/* The join keys, each said in words: the bare `forge` or `success`
              read as wire noise until the reader was told which was which. */}
          <dl className="activity-row__facts">
            <div className="activity-row__field">
              <dt>Source</dt>
              <dd>{event.source}</dd>
            </div>
            <div className="activity-row__field">
              <dt>Kind</dt>
              <dd>{event.kind}</dd>
            </div>
            {event.outcome ? (
              <div className="activity-row__field">
                <dt>Outcome</dt>
                <dd>{event.outcome}</dd>
              </div>
            ) : null}
            {event.actor ? (
              <div className="activity-row__field">
                <dt>Actor</dt>
                <dd>{event.actor}</dd>
              </div>
            ) : null}
            {event.sha ? (
              <div className="activity-row__field">
                <dt>Commit</dt>
                <dd>
                  <code title={event.sha}>{shortSha(event.sha)}</code>
                </dd>
              </div>
            ) : null}
            {others.length > 0 ? (
              <div className="activity-row__field">
                <dt>Links</dt>
                <dd>
                  {others.map((link) => (
                    <Link key={link.to} to={link.to} className="activity-row__link">
                      {link.label}
                    </Link>
                  ))}
                </dd>
              </div>
            ) : null}
          </dl>
          {event.log_tail ? (
            <div className="activity-row__log-box">
              <p className="activity-row__log-bar">
                <span className="activity-row__meta">
                  {badLines === 0 ? 'Log tail' : `Log tail — ${badLines} failing line${badLines === 1 ? '' : 's'}`}
                </span>
                <CopyButton
                  text={event.log_tail}
                  label={`event ${event.seq} log`}
                  testId={`activity-event-log-copy-${event.seq}`}
                >
                  Copy log
                </CopyButton>
              </p>
              <pre
                className="activity-row__log"
                ref={logRef}
                tabIndex={0}
                data-testid={`activity-event-log-${event.seq}`}
                aria-label={`Log tail of event ${event.seq}`}
              >
                {lines.map((line, index) => (
                  <span
                    key={index}
                    className={`activity-row__log-line${line.bad ? ' activity-row__log-line--bad' : ''}`}
                  >
                    {line.text === '' ? '\u00a0' : line.text}
                  </span>
                ))}
              </pre>
            </div>
          ) : null}
          {event.log_url && /^(https?:\/\/|\/)/.test(event.log_url) ? (
            <a href={event.log_url} target="_blank" rel="noreferrer">
              Full log
            </a>
          ) : null}
          {/* The row in the words the API uses, for a ticket, a script or an
              agent: the event exactly as the feed received it. */}
          <p className="activity-row__json">
            <CopyButton
              text={eventJson(event)}
              label={`event ${event.seq} as JSON`}
              testId={`activity-event-json-${event.seq}`}
            >
              Copy JSON
            </CopyButton>
          </p>
        </div>
      ) : null}
    </li>
  );
}
